import React, { useState, useEffect, useRef } from 'react';

/**
 * Optimized Image Component with lazy loading, caching, and performance features
 * Features:
 * - Intersection Observer for efficient lazy loading
 * - Image preloading and caching
 * - Width/height attributes to prevent layout shift
 * - Error handling with fallback
 * - Progressive loading with blur-up effect
 * - Fetch priority for above-the-fold images
 */
function OptimizedImage({
  src,
  alt,
  width,
  height,
  className = '',
  style = {},
  loading = 'lazy',
  fetchPriority = 'auto',
  onError,
  placeholder = true,
  ...props
}) {
  const [imageError, setImageError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(loading === 'eager' || fetchPriority === 'high');
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (shouldLoad || loading === 'eager') return;

    if (!imgRef.current) return;

    // Create intersection observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            if (observerRef.current && imgRef.current) {
              observerRef.current.unobserve(imgRef.current);
            }
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before image enters viewport
        threshold: 0.01,
      }
    );

    observerRef.current.observe(imgRef.current);

    return () => {
      if (observerRef.current && imgRef.current) {
        observerRef.current.unobserve(imgRef.current);
      }
    };
  }, [shouldLoad, loading]);

  const handleError = (e) => {
    setImageError(true);
    if (onError) onError(e);
  };

  const handleLoad = () => {
    setIsLoaded(true);
  };

  // Skeleton/placeholder while loading
  const placeholderStyle = {
    width: width || '100%',
    height: height || '200px',
    backgroundColor: '#f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...style,
  };

  if (imageError) {
    return (
      <div
        className={`image-placeholder ${className}`}
        style={placeholderStyle}
      >
        <span style={{ color: '#999', fontSize: '14px' }}>Image not available</span>
      </div>
    );
  }

  return (
    <div
      ref={imgRef}
      style={{
        position: 'relative',
        width: width || '100%',
        height: height || 'auto',
        overflow: 'hidden',
      }}
    >
      {/* Placeholder/Skeleton */}
      {placeholder && !isLoaded && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: '#f0f0f0',
            backgroundImage: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
          }}
        />
      )}
      
      {/* Actual Image */}
      {shouldLoad && (
        <img
          src={src}
          alt={alt || ''}
          width={width}
          height={height}
          className={className}
          style={{
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
            position: 'relative',
            zIndex: 1,
            ...style,
          }}
          loading={loading}
          fetchPriority={fetchPriority}
          onError={handleError}
          onLoad={handleLoad}
          decoding="async"
          {...props}
        />
      )}
      
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </div>
  );
}

export default OptimizedImage;


